/**
 * Implement a class that represents a Cerebras AI client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import Cerebras from '@cerebras/cerebras_cloud_sdk';
import { nanoid } from "nanoid";
import { zodToJsonSchema } from "zod-to-json-schema";
import { Message, type ToolCall } from "../message";
import { Thread } from "../thread";
import { Tool } from "../tool";
import { BaseModelDriver } from "./base";
import { traceable } from "../tracing";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;

/**
 * Available Cerebras models
 */
export type CerebrasModel =
  | "llama-4-scout-17b-16e-instruct"  // Llama 4 Scout (109B parameters)
  | "llama3.1-8b"                     // Llama 3.1 8B (8B parameters)
  | "llama-3.3-70b"                   // Llama 3.3 70B (70B parameters)
  | "deepSeek-r1-distill-llama-70B";  // DeepSeek R1 Distill Llama 70B

/**
 * Configuration options for the Cerebras driver
 */
export interface CerebrasConfig {
  /**
   * Cerebras API Key
   */
  apiKey?: string;

  /**
   * Model to use
   * @default "llama3.1-8b"
   */
  model?: CerebrasModel | string;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Enable tracing for this driver
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;

  /**
   * Disable parallel tool calls
   * Some Cerebras models don't support parallel tool calls
   * @default false
   */
  disableParallelToolCalls?: boolean;

  /**
   * Maximum number of tool calls allowed per thread to prevent infinite loops
   * @default 10
   */
  maxTotalToolCalls?: number;

  /**
   * Optional JSON schema to enforce for the response format.
   * See: https://inference-docs.cerebras.ai/capabilities/structured-outputs
   */
  responseFormatSchema?: Record<string, any>;

  /**
   * Optional name for the JSON schema provided in responseFormatSchema.
   * Used if responseFormatSchema is set.
   * @default "improv_schema"
   */
  responseFormatName?: string;

  /**
   * Optional flag to enforce strict adherence to the responseFormatSchema.
   * Used if responseFormatSchema is set.
   * @default true
   */
  responseFormatStrict?: boolean;

  /**
   * Whether to run tool calls in parallel
   * @default false
   */
  parallelToolCalls?: boolean;
}

/**
 * Response types for Cerebras API
 */
interface CerebrasToolCall {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
  index?: number;
}

interface CerebrasMessage {
  role: string;
  content: string;
  tool_calls?: CerebrasToolCall[];
}

interface CerebrasChoice {
  message: CerebrasMessage;
  index: number;
  finish_reason: string;
}

interface CerebrasStreamingChoice {
  delta: {
    content?: string;
    tool_calls?: CerebrasToolCall[];
  };
  index: number;
  finish_reason?: string;
}

interface CerebrasUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

interface CerebrasResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: CerebrasChoice[];
  usage: CerebrasUsage;
}

interface CerebrasStreamingResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: CerebrasStreamingChoice[];
}

/**
 * Represents a Cerebras AI client and provides tools for converting messages to/from the LLM
 */
export class CerebrasThreadDriver extends BaseModelDriver {
  /**
   * Cerebras API Client
   */
  private client: Cerebras;

  /**
   * Cerebras model to use
   */
  private model: CerebrasModel | string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;

  /**
   * Whether parallel tool calls are disabled
   */
  private disableParallelToolCalls: boolean;
  
  /**
   * Track recent tool calls to prevent infinite loops
   */
  private recentToolCalls: Array<{
    name: string;
    argsHash: string;
    timestamp: number;
  }> = [];
  
  /**
   * Timeframe for tracking repeated tool calls (in milliseconds)
   */
  private toolCallTimeframe = 60000; // 1 minute

  /**
   * Maximum number of tool calls allowed per thread to prevent infinite loops
   */
  private maxTotalToolCalls = 10;
  
  /**
   * Counter for total number of tool calls processed in the current thread
   */
  private totalToolCallsCounter = 0;

  /**
   * Whether to run tool calls in parallel
   */
  public parallelToolCalls: boolean;

  /**
   * Key-value store to track repeat tool calls by their hash
   * The key is toolName:argsHash and the value is the count
   */
  private toolCallTracker: Record<string, number> = {};

  /**
   * Optional JSON schema for structured outputs.
   */
  private responseFormatSchema?: Record<string, any>;

  /**
   * Name for the response format schema.
   */
  private responseFormatName: string;

  /**
   * Strictness for the response format schema.
   */
  private responseFormatStrict: boolean;

  /**
   * Returns a list of available Cerebras models
   */
  static getAvailableModels(): CerebrasModel[] {
    return [
      "llama-4-scout-17b-16e-instruct",
      "llama3.1-8b", 
      "llama-3.3-70b",
      "deepSeek-r1-distill-llama-70B"
      // Note: "Llama 4 Maverick" will be added when available
    ];
  }

  /**
   * Create a new Cerebras driver
   * @param config Configuration options
   */
  constructor(config: CerebrasConfig = {}) {
    super();

    // Get API Key from config or environment variable
    const apiKey = config.apiKey || process.env.CEREBRAS_API_KEY;
    if (!apiKey) {
      throw new Error(
        "Cerebras API Key must be provided in config or as CEREBRAS_API_KEY environment variable"
      );
    }

    // Create base client
    const baseClient = new Cerebras({
      apiKey
    });
    
    // Only wrap with tracing if trace is enabled
    if (config.trace ?? false) {
      // Create a proxy around the Cerebras client to intercept API calls
      this.client = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          
          // Handle nested properties for chat completions
          if (prop === 'chat' && value && typeof value === 'object') {
            return new Proxy(value, {
              get: (chatTarget, chatProp, chatReceiver) => {
                const chatValue = Reflect.get(chatTarget, chatProp, chatReceiver);
                
                // Handle completions property
                if (chatProp === 'completions' && chatValue && typeof chatValue === 'object') {
                  return new Proxy(chatValue, {
                    get: (completionsTarget, completionsProp, completionsReceiver) => {
                      const completionsValue = Reflect.get(completionsTarget, completionsProp, completionsReceiver);
                      
                      // Wrap the create method with tracing
                      if (completionsProp === 'create' && typeof completionsValue === 'function') {
                        return traceable(completionsValue.bind(completionsTarget), {
                          run_type: "llm",
                          name: "cerebras",
                          ...config.traceMetadata,
                          aggregator: (args: any[], result: any) => {
                            // Extract the request parameters
                            const params = args[0] || {};
                            
                            // For streaming responses
                            if (params.stream) {
                              return {
                                model: params.model,
                                input: {
                                  messages: params.messages,
                                  temperature: params.temperature,
                                  max_tokens: params.max_tokens,
                                  tools: params.tools ? params.tools.length : 0,
                                },
                                streaming: true
                              };
                            }
                            
                            // For standard responses
                            return {
                              model: params.model,
                              input: {
                                messages: params.messages,
                                temperature: params.temperature,
                                max_tokens: params.max_tokens,
                                tools: params.tools ? params.tools.length : 0,
                              },
                              output: result.choices?.[0]?.message?.content,
                              metrics: result.usage ? {
                                prompt_tokens: result.usage.prompt_tokens,
                                completion_tokens: result.usage.completion_tokens,
                                total_tokens: result.usage.total_tokens,
                              } : undefined,
                              finish_reason: result.choices?.[0]?.finish_reason
                            };
                          }
                        });
                      }
                      
                      return completionsValue;
                    }
                  });
                }
                
                return chatValue;
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.client = baseClient;
    }
    
    // Set configuration options
    this.model = config.model || "llama3.1-8b";
    this.temperature = config.temperature ?? 0.7;
    this.maxTokens = config.maxTokens;
    this.cache = config.cache ?? false;
    this.disableParallelToolCalls = config.disableParallelToolCalls ?? false;
    this.responseFormatSchema = config.responseFormatSchema;
    this.responseFormatName = config.responseFormatName || "improv_schema";
    this.responseFormatStrict = config.responseFormatStrict ?? true;
    this.maxTotalToolCalls = config.maxTotalToolCalls ?? 10;
    this.parallelToolCalls = config.parallelToolCalls ?? false;
    
    // Adjust settings for specific models known to have issues
    this.configureModelSpecificSettings(config);
    
    // Log the model being used
    this.debug(`Using Cerebras model: ${this.model}`);
  }

  /**
   * Configure model-specific settings to handle known issues
   */
  private configureModelSpecificSettings(config: CerebrasConfig) {
    // Handle specific models with known issues
    if (this.model === "llama-3.3-70b") {
      this.debug("Applying specific settings for llama-3.3-70b model");
      
      // This model has known issues with tool calls - very strict settings
      this.disableParallelToolCalls = false; // Force sequential tool calls for this model
      this.maxTotalToolCalls = config.maxTotalToolCalls ?? 20; // Lower total limit for this model
      
      // Change the tool call timeframe to be more strict
      this.toolCallTimeframe = 30000; // 30 seconds
    }
  }

  /**
   * Try several fallback strategies to handle errors.
   * This is useful for working around limitations or issues with the Cerebras API.
   * 
   * @param params Original API parameters
   * @param apiError Original API error
   * @returns CerebrasResponse from a successful strategy, or throws if all strategies fail
   */
  private async tryFallbackStrategies(params: any, apiError: any): Promise<CerebrasResponse> {
    // Strategy 1: Try with simplified tool definitions (string types only)
    try {
      this.debug("Fallback Strategy 1: Simplified tool definitions", { error: apiError.message });
      
      const strategy1Params = JSON.parse(JSON.stringify(params)); // Deep clone
      
      // Simplify tool definitions
      if (strategy1Params.tools) {
        for (const tool of strategy1Params.tools) {
          if (tool.function && tool.function.parameters && tool.function.parameters.properties) {
            // Ensure all property types are 'string'
            for (const key in tool.function.parameters.properties) {
              tool.function.parameters.properties[key].type = 'string';
              
              // Remove any complex nested structures
              delete tool.function.parameters.properties[key].properties;
              delete tool.function.parameters.properties[key].items;
            }
          }
        }
      }
      
      return await this.client.chat.completions.create(strategy1Params) as CerebrasResponse;
    } catch (error) {
      const error1 = error as any;
      this.debug("Fallback Strategy 1 failed", { error: error1.message });
      
      // Strategy 2: Try with minimal tool definitions (remove most parameters)
      try {
        this.debug("Fallback Strategy 2: Minimal tool definitions", { error: apiError.message });
        
        const strategy2Params = JSON.parse(JSON.stringify(params)); // Deep clone
        
        // Simplify tool definitions to bare minimum
        if (strategy2Params.tools) {
          for (const tool of strategy2Params.tools) {
            if (tool.function && tool.function.parameters) {
              // Keep only the type and an empty properties object
              tool.function.parameters = {
                type: "object",
                properties: {}
              };
            }
          }
        }
        
        return await this.client.chat.completions.create(strategy2Params) as CerebrasResponse;
      } catch (error) {
        const error2 = error as any;
        this.debug("Fallback Strategy 2 failed", { error: error2.message });
        
        // Strategy 3: Try without any tools
        try {
          this.debug("Fallback Strategy 3: No tools, just content", { error: apiError.message });
          
          const strategy3Params = {
            model: params.model,
            temperature: params.temperature,
            messages: params.messages,
            // No tools, no tool_choice, no parallel_tool_calls
          };
          
          return await this.client.chat.completions.create(strategy3Params) as CerebrasResponse;
        } catch (error) {
          const error3 = error as any;
          this.debug("Fallback Strategy 3 failed", { error: error3.message });
          
          // Strategy 4: Final emergency fallback - use only the last message
          try {
            this.debug("Fallback Strategy 4: Emergency simplified request", { error: apiError.message });
            
            const lastUserMessage = params.messages.filter((msg: any) => msg.role === 'user').pop();
            const simplifiedContent = lastUserMessage?.content 
              ? String(lastUserMessage.content).replace(/@\{\{\{.*?\}\}\}/g, "[FIELD]") 
              : "Help me with this request";
            
            // Use 'any' type for strategy4Params to avoid typing issues
            const strategy4Params: any = {
              model: params.model,
              temperature: params.temperature,
              messages: [
                {
                  role: "system",
                  content: "You are a helpful AI assistant. The user may send you field references like [FIELD] that you should consider as data fields."
                },
                {
                  role: "user",
                  content: simplifiedContent
                }
              ]
            };
            
            return await this.client.chat.completions.create(strategy4Params) as CerebrasResponse;
          } catch (error) {
            const error4 = error as any;
            this.debug("All fallback strategies failed", { 
              originalError: apiError.message,
              finalError: error4.message
            });
            
            // If all strategies fail, throw the original error
            throw apiError;
          }
        }
      }
    }
  }

  /**
   * Hash the tool call arguments to compare for duplicates
   */
  private hashToolCallArgs(args: any): string {
    try {
      // Sort keys to ensure consistent order
      const normalized = typeof args === 'object' && args !== null
        ? JSON.stringify(args, Object.keys(args).sort())
        : String(args);
      
      // Simple hash function for string
      let hash = 0;
      for (let i = 0; i < normalized.length; i++) {
        const char = normalized.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return hash.toString(16);
    } catch (e) {
      // If hashing fails, use string representation
      return String(args);
    }
  }
  
  /**
   * Check if a tool call has been made before with the same arguments
   * Returns true for any repeat calls, allowing only the first occurrence
   */
  private isRepeatedToolCall(name: string, args: any): boolean {
    // Clean up old entries from timestamp-based tracking
    const now = Date.now();
    this.recentToolCalls = this.recentToolCalls.filter(call => 
      now - call.timestamp < this.toolCallTimeframe
    );
    
    // Hash the arguments
    const argsHash = this.hashToolCallArgs(args);
    
    // Create a unique key for this tool call
    const callKey = `${name}:${argsHash}`;
    
    // Check if this tool call has been seen before
    const identicalCallCount = this.toolCallTracker[callKey] || 0;
    
    // Also use the timestamp-based tracking as a backup
    const timestampBasedCalls = this.recentToolCalls.filter(call => 
      call.name === name && call.argsHash === argsHash
    );
    
    // If this is NOT a repeated call, we should still track it for future reference
    // But we do this AFTER checking to avoid counting the current call in our check
    if (identicalCallCount === 0 && timestampBasedCalls.length === 0) {
      // Log the first occurrence
      this.debug(`First occurrence of tool call for ${name}`, {
        argsHash,
        args: typeof args === 'object' ? JSON.stringify(args) : args
      });
    } else if (identicalCallCount > 0) {
      // Log repeated tool detection
      this.debug(`Detected repeated tool call for ${name}`, {
        callCount: identicalCallCount,
        argsHash,
        args: typeof args === 'object' ? JSON.stringify(args) : args
      });
    }
    
    // AFTER checking, update the trackers for future checks
    this.toolCallTracker[callKey] = identicalCallCount + 1;
    
    // Add current call to timestamp history
    this.recentToolCalls.push({
      name,
      argsHash,
      timestamp: now
    });
    
    // Return true only if this is actually a repeat (not the first occurrence)
    return identicalCallCount > 0 || timestampBasedCalls.length > 0;
  }

  /**
   * Simplifies the message history after a tool call to prevent model confusion.
   * Replaces the original user request and assistant tool call with a summary.
   */
  private simplifyMessagesAfterToolCall(messages: any[]): any[] {
    const simplifiedMessages = [];
    let foundToolResult = false;
    
    // Iterate backwards to find the last tool result message
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'tool') {
        foundToolResult = true;
        // Keep the tool result message
        simplifiedMessages.unshift(messages[i]);
        
        // Find the corresponding assistant message with the tool_calls
        let assistantMsgIndex = -1;
        for (let j = i - 1; j >= 0; j--) {
          if (messages[j].role === 'assistant' && messages[j].tool_calls?.some((tc: any) => tc.id === messages[i].tool_call_id)) {
            assistantMsgIndex = j;
            break;
          }
        }
        
        if (assistantMsgIndex !== -1) {
          // Create a summary message instead of the original assistant/user requests
          let toolParams = "{}";
          try {
            // Try to parse and format the parameters nicely
            const args = messages[assistantMsgIndex].tool_calls[0].function.arguments;
            if (typeof args === 'string') {
              const parsedArgs = JSON.parse(args);
              toolParams = JSON.stringify(parsedArgs, null, 2);
            } else {
              toolParams = JSON.stringify(args, null, 2);
            }
          } catch (e) {
            // If parsing fails, use the raw string
            toolParams = messages[assistantMsgIndex].tool_calls[0].function.arguments || "{}";
          }
          
          simplifiedMessages.unshift({
            role: 'assistant',
            content: `I have completed the previous action using the ${messages[assistantMsgIndex].tool_calls[0].function.name} tool with parameters: ${toolParams}`
          });
          // Skip the original assistant message and potentially the user message before it
          i = assistantMsgIndex; 
          if (messages[i-1]?.role === 'user') {
            i--; // Also skip the user message if it likely triggered the tool call
          }
        } else {
          // If couldn't find assistant message, keep the tool message but log a warning
          this.debug("Could not find matching assistant message for tool result", { tool_call_id: messages[i].tool_call_id });
        }
      } else {
        // Keep other messages
        simplifiedMessages.unshift(messages[i]);
      }
      
      // If we processed the tool result, stop simplifying further back
      if (foundToolResult) break;
    }
    
    // If we didn't find a tool result (shouldn't happen in the intended flow, but safeguard)
    if (!foundToolResult) {
        return messages; 
    }
    
    // Return the potentially simplified message history
    // Ensure the system message is always first if present
    const systemMessage = messages.find(m => m.role === 'system');
    const finalMessages = simplifiedMessages.filter(m => m.role !== 'system');
    if (systemMessage) {
        finalMessages.unshift(systemMessage);
    }

    return finalMessages;
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    this.debug("Sending message to Cerebras API", { model: this.model });

    // Check if we have a completely new conversation
    const isNewConversation = thread.all().length <= 1;
    
    if (isNewConversation) {
      // Reset all tracking for new conversations
      this.debug("Starting new conversation, resetting all tracking");
      this.recentToolCalls = [];
      this.totalToolCallsCounter = 0;
      this.toolCallTracker = {};
    } else {
      // Examine if this is a fresh session that should reset counters
      
      // Check if there are any completed tool calls in the history
      let hasCompletedToolCalls = false;
      
      for (const msg of thread.all()) {
        // Check if any message contains tool calls that have been executed
        if (msg.toolResults && msg.toolResults.length > 0) {
          hasCompletedToolCalls = true;
          break;
        }
      }
      
      if (!hasCompletedToolCalls) {
        // If no completed tool calls in history, this is effectively a new conversation
        this.debug("No completed tool calls in history, resetting tool counters");
        this.totalToolCallsCounter = 0;
        // We don't reset usedToolNames here because we still want to avoid repeats
      }
    }
    
    // For safety, examine the existing thread for already executed tool calls
    // This helps us maintain tracking even when the driver instance persists across multiple API calls
    if (!isNewConversation) {
      // Count all existing tool calls in the thread
      let existingToolCalls = 0;
      
      for (const msg of thread.all()) {
        if (msg.toolCalls && msg.toolCalls.length > 0) {
          existingToolCalls += msg.toolCalls.length;
          
          // Also track each tool call's signature
          for (const call of msg.toolCalls) {
            const argsHash = this.hashToolCallArgs(call.arguments);
            const callKey = `${call.name}:${argsHash}`;
            this.toolCallTracker[callKey] = (this.toolCallTracker[callKey] || 0) + 1;
          }
        }
      }
      
      // Use this to initialize our counter if it's not already set
      if (this.totalToolCallsCounter < existingToolCalls) {
        this.totalToolCallsCounter = existingToolCalls;
        this.debug("Updated tool call counter from thread history", { count: existingToolCalls });
      }
      
      // Log the current state of tool counts
      this.debug("Current tool call counts", {
        totalToolCallsCounter: this.totalToolCallsCounter,
        maxLimit: this.maxTotalToolCalls
      });
    }

    // Format messages for Cerebras API
    let messages = this.formatMessagesForAPI(thread);
    
    // Check if the last message is a tool result, indicating we should simplify history
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'tool') {
      this.debug("Simplifying message history after tool result");
      messages = this.simplifyMessagesAfterToolCall(messages);
    }

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        // Preprocess tools to handle special field references
        const preprocessedTools = this.preprocessToolDefinitions(tools);
        params.tools = this.createToolDefinitions(preprocessedTools);
        
        // Handle tool choice based on parallel tool call setting
        if (this.disableParallelToolCalls) {
          params.parallel_tool_calls = false; // Forces model to use exactly one tool
          params.tool_choice = "auto"; 
        } else {
          params.tool_choice = "auto"; // Default behavior
        }
      } else if (this.responseFormatSchema) {
        // Only apply response_format if NO tools are being used, as per Cerebras docs
        params.response_format = {
          type: "json_schema",
          json_schema: {
            name: this.responseFormatName,
            strict: this.responseFormatStrict,
            schema: this.responseFormatSchema
          }
        };
        this.debug("Applying structured response_format", { name: this.responseFormatName, strict: this.responseFormatStrict });
      }

      // Log the full request parameters before making the API call
      this.debug("Cerebras API request parameters", {
        model: params.model,
        temperature: params.temperature,
        max_tokens: params.max_tokens,
        parallel_tool_calls: params.parallel_tool_calls,
        tool_choice: params.tool_choice,
        toolsCount: params.tools ? params.tools.length : 0,
        messageCount: params.messages.length,
        // Log a sample of the tool definitions
        toolsSample: params.tools ? params.tools.slice(0, 1).map((tool: any) => ({
          type: tool.type,
          functionName: tool.function?.name,
          parametersSchema: tool.function?.parameters
        })) : null,
        // Log a sample of the messages
        messagesSample: params.messages.slice(-3).map((msg: any) => ({
          role: msg.role,
          contentLength: msg.content ? (typeof msg.content === 'string' ? msg.content.length : JSON.stringify(msg.content).length) : 0,
          hasToolCalls: !!msg.tool_calls,
          toolCallCount: msg.tool_calls ? msg.tool_calls.length : 0
        }))
      });

      // Declare response variable outside try block so it's accessible in the outer scope
      let response: CerebrasResponse | undefined;
      
      try {
        // Make the API call
        try {
          response = await this.client.chat.completions.create(params) as CerebrasResponse;
        } catch (apiError: any) {
          // Log all details for debugging
          this.error("Cerebras API error details", {
            error: apiError.message,
            code: apiError.error?.code,
            statusCode: apiError.statusCode,
            failed_generation: apiError.error?.failed_generation || 'not available',
            type: apiError.type,
            param: apiError.param
          });
          
          try {
            // Try various fallback strategies
            response = await this.tryFallbackStrategies(params, apiError);
            this.debug("Fallback strategy succeeded", { 
              model: response.model, 
              responseType: response.object,
              choiceCount: response.choices?.length || 0
            });
          } catch (fallbackError) {
            // All fallback strategies failed, re-throw original error
            throw apiError;
          }
        }
        
        // Log the raw response for debugging
        this.debug("Cerebras API raw response", {
          id: response.id,
          model: response.model,
          response: response,
          firstChoiceContent: response.choices?.[0]?.message?.content,
          hasTool: !!response.choices?.[0]?.message?.tool_calls,
          finishReason: response.choices?.[0]?.finish_reason
        });
        
        // Get the last choice from the response
        const lastChoice = response.choices[0];
        if (!lastChoice) {
          throw new Error("No response from Cerebras API");
        }

        const choice = lastChoice.message;
        if (!choice) {
          throw new Error("Empty response from Cerebras API");
        }

        // Determine tool calls based PRIMARILY on finish_reason and explicit tool_calls
        let finalToolCalls: ToolCall[] | undefined = undefined;

        if (lastChoice.finish_reason === 'tool_calls' && choice.tool_calls) {
            // Primary case: Model explicitly signals tool calls
            finalToolCalls = this.parseToolCalls(choice.tool_calls);
            this.debug("Using explicit tool_calls from API response", { count: finalToolCalls?.length });
        } else if (choice.content) {
            // Fallback: Try to parse embedded tool calls from content,
            // regardless of finish_reason, but only if no explicit tool_calls were provided.
            // This handles cases where the model might output a tool call in text
            // even with finish_reason: "stop".
            const embeddedCalls = this.parseEmbeddedToolCalls(choice.content);
            if (embeddedCalls && embeddedCalls.length > 0) {
                finalToolCalls = embeddedCalls;
                this.debug("Using embedded tool_calls parsed from content", { count: finalToolCalls?.length, finish_reason: lastChoice.finish_reason });
                // If we found embedded calls, the actual content should be considered empty
                // choice.content = ""; // Let message constructor handle this based on finalToolCalls
            }
        }

        // Create a new message from the response
        const message = new Message({
          role: "assistant",
          // If we identified tool calls (either explicit or embedded), content should be empty.
          // Otherwise, use the model's text content.
          content: finalToolCalls && finalToolCalls.length > 0 ? "" : (choice.content || ""),
          toolCalls: finalToolCalls,
          cache: this.cache,
        });

        // Check for potential infinite tool call loops
        if (message.toolCalls && message.toolCalls.length > 0) {
          // First check if we've exceeded the total tool call limit
          // Update the counter using our new method that excludes utility tools
          this.updateToolCallCounter(message.toolCalls);
          
          if (this.totalToolCallsCounter > this.maxTotalToolCalls) {
            this.debug("Exceeded maximum total tool calls limit", {
              count: this.totalToolCallsCounter,
              limit: this.maxTotalToolCalls,
              model: this.model
            });
            
            // Replace with a message explaining the situation
            const limitMessage = new Message({
              role: "assistant",
              content: "I've reached the maximum number of tool calls allowed for this conversation. This suggests there might be a loop in my reasoning. Let me summarize without using more tools.",
              cache: this.cache,
            });
            
            // Replace the original message
            Object.assign(message, limitMessage);
          } else {
            // Check each tool call to see if it's a repeat
            const safeToolCalls = message.toolCalls.filter(toolCall => {
              const isRepeated = this.isRepeatedToolCall(toolCall.name, toolCall.arguments);
              if (isRepeated) {
                this.debug(`Filtering out repeated tool call: ${toolCall.name}`, {
                  toolUseId: toolCall.toolUseId,
                  isFirstUse: false
                });
              } else {
                this.debug(`Allowing tool call: ${toolCall.name}`, {
                  toolUseId: toolCall.toolUseId,
                  isFirstUse: true
                });
              }
              return !isRepeated;
            });
            
            // If we filtered out any tool calls, replace the message
            if (safeToolCalls.length < message.toolCalls.length) {
              this.debug("Filtered out repeated tool calls", {
                modelName: this.model,
                originalCount: message.toolCalls.length,
                filteredCount: safeToolCalls.length,
                toolNames: message.toolCalls.map(tc => tc.name),
                safeToolNames: safeToolCalls.map(tc => tc.name)
              });
              
              if (safeToolCalls.length > 0) {
                // Keep non-repeating tool calls
                const updatedMessage = new Message({
                  role: "assistant",
                  content: message.content,
                  toolCalls: safeToolCalls,
                  cache: this.cache,
                });
                
                // Replace the original message
                Object.assign(message, updatedMessage);
              } else {
                // If all tool calls were repeating, replace with a text response
                const noRepeatMessage = new Message({
                  role: "assistant",
                  content: "I noticed that I would be calling tools that have already been used. Let's approach this differently without using repeated tools. How else can I help you with your request?",
                  cache: this.cache,
                });
                
                // Replace the original message
                Object.assign(message, noRepeatMessage);
              }
            }
          }
        }

        // If we found tool calls extracted from content, clear the content to avoid duplication
        if (!choice.tool_calls && message.toolCalls && message.toolCalls.length > 0) {
          // Filter out any repeated tool calls from the extracted ones
          const safeToolCalls = message.toolCalls.filter(toolCall => {
            const isRepeated = this.isRepeatedToolCall(toolCall.name, toolCall.arguments);
            if (isRepeated) {
              this.debug(`Filtering out repeated tool call from content: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: false
              });
            } else {
              this.debug(`Allowing tool call from content: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: true
              });
            }
            return !isRepeated;
          });
          
          if (safeToolCalls.length > 0) {
            // Tool calls were extracted from content and at least some are not repeats,
            // so create a new message with the safe tool calls
            const updatedMessage = new Message({
              role: "assistant",
              content: "", // Empty content since the content was actually a tool call
              toolCalls: safeToolCalls,
              cache: this.cache,
            });
            
            // Log details of the detected tool calls
            this.debug("Extracted tool calls from message content", {
              totalToolCount: message.toolCalls.length,
              safeToolCount: safeToolCalls.length,
              toolNames: safeToolCalls.map(t => t.name),
              originalContentLength: choice.content?.length || 0
            });
            
            // Add the updated message to the thread
            thread.push(updatedMessage);
          } else {
            // All extracted tool calls were repeats, so create a text message instead
            const noRepeatMessage = new Message({
              role: "assistant",
              content: "I was going to use some tools to help with your request, but I noticed they would be repeats of tools already used. Let me help you directly instead.",
              cache: this.cache,
            });
            
            // Add the message to the thread
            thread.push(noRepeatMessage);
          }
        } else {
          // Add the original message to the thread
          thread.push(message);
        }
      } catch (error: any) {
        // Log all available error details
        this.error("Cerebras API error details", {
          error: error.message,
          code: error.error?.code,
          statusCode: error.statusCode,
          failed_generation: error.error?.failed_generation || 'not available',
          type: error.type,
          param: error.param,
          raw: JSON.stringify(error)
        });

        // Check if this is a tool call error with failed_generation information
        if (error?.error?.code === 'tool_use_failed' && 
            error?.error?.failed_generation && 
            tools && tools.length > 0) {
          
          this.debug("Handling tool call error with failed_generation", { 
            failed_generation: error.error.failed_generation 
          });
          
          try {
            // Parse the failed_generation field which contains the tool call info
            // It might be a string or already an object
            let failedGeneration: any;
            if (typeof error.error.failed_generation === 'string') {
              try {
                failedGeneration = JSON.parse(error.error.failed_generation);
              } catch (e) {
                // If it's not valid JSON, try to recover by parsing with a regex
                const nameMatch = /["']?name["']?\s*:\s*["']([^"']+)["']/.exec(error.error.failed_generation);
                const argsMatch = /["']?arguments["']?\s*:\s*["']?({[^}]+})["']?/.exec(error.error.failed_generation);
                const paramsMatch = /["']?parameters["']?\s*:\s*["']?({[^}]+})["']?/.exec(error.error.failed_generation);
                
                if (nameMatch && nameMatch[1]) {
                  failedGeneration = {
                    name: nameMatch[1],
                    arguments: argsMatch && argsMatch[1] ? JSON.parse(argsMatch[1]) : {},
                    parameters: paramsMatch && paramsMatch[1] ? JSON.parse(paramsMatch[1]) : {}
                  };
                } else {
                  throw e; // Can't recover, rethrow
                }
              }
            } else {
              failedGeneration = error.error.failed_generation;
            }
            
            // Check if the failed generation has the expected format
            // Look for name and arguments/parameters in multiple possible locations
            const toolName = failedGeneration.name || 
                            (failedGeneration.function && failedGeneration.function.name);
            
            let toolArgs: any = {};
            
            // Check for arguments in multiple possible locations
            if (failedGeneration.arguments) {
              toolArgs = typeof failedGeneration.arguments === 'string' 
                ? JSON.parse(failedGeneration.arguments) 
                : failedGeneration.arguments;
            } else if (failedGeneration.parameters) {
              toolArgs = typeof failedGeneration.parameters === 'string' 
                ? JSON.parse(failedGeneration.parameters) 
                : failedGeneration.parameters;
            } else if (failedGeneration.function) {
              if (failedGeneration.function.arguments) {
                toolArgs = typeof failedGeneration.function.arguments === 'string' 
                  ? JSON.parse(failedGeneration.function.arguments) 
                  : failedGeneration.function.arguments;
              } else if (failedGeneration.function.parameters) {
                toolArgs = typeof failedGeneration.function.parameters === 'string' 
                  ? JSON.parse(failedGeneration.function.parameters) 
                  : failedGeneration.function.parameters;
              }
            }
            
            if (toolName) {
              // Find the matching tool
              const matchingTool = tools.find(t => t.getName() === toolName);
              
              if (matchingTool) {
                // Instead of creating a tool call message that would trigger automatic execution,
                // we'll manually execute the tool and create a message with both the tool call and result
                
                // Generate a unique tool use ID
                const toolUseId = nanoid();
                
                try {
                  // Execute the tool directly
                  this.debug(`Manually executing tool ${toolName}`, toolArgs);
                  // Create a tool call object
                  const toolCall = {
                    toolUseId,
                    name: toolName,
                    arguments: toolArgs
                  };
                  const toolResult = await matchingTool.executeTool(toolArgs, toolCall);
                  
                  // Extract the result from the toolResult object
                  const result = toolResult.success ? toolResult.result : null;
                  
                  // Create a descriptive content based on the tool and result
                  let content = `I'll help with that using the ${toolName} tool.\n\n`;
                  
                  // Generic result formatting for all tools
                  if (result !== null) {
                    // Format differently based on result type
                    if (typeof result === 'object') {
                      content += `Here's the result: ${JSON.stringify(result, null, 2)}`;
                    } else {
                      content += `The result is: ${result}`;
                    }
                  } else if (toolResult.error) {
                    content += `There was an error executing the tool: ${toolResult.error}`;
                  }
                  
                  // Create first message that would normally come from the assistant
                  const assistantMessage = new Message({
                    role: "assistant",
                    content: `I'll help with that using the ${toolName} tool.`,
                    toolCalls: [{
                      toolUseId,
                      name: toolName,
                      arguments: toolArgs
                    }],
                    cache: this.cache,
                  });
                  
                  // Create second message that would normally come from the user with tool results
                  const toolResultMessage = new Message({
                    role: "user",
                    content: "",
                    toolResults: [{
                      toolUseId,
                      name: toolName,
                      result: result
                    }],
                    cache: this.cache,
                  });
                  
                  // Create final assistant message with the formatted result
                  const finalMessage = new Message({
                    role: "assistant",
                    content,
                    cache: this.cache,
                  });
                  
                  // Add the sequence of messages to the thread
                  thread.push(assistantMessage);
                  thread.push(toolResultMessage);
                  thread.push(finalMessage);
                  
                  this.debug("Successfully executed tool and created result message");
                  
                  // Log usage metrics if available
                  if (error.error.usage) {
                    const usage = error.error.usage;
                    this.debug("Cerebras API usage metrics", {
                      inputTokens: usage.prompt_tokens,
                      outputTokens: usage.completion_tokens || 0,
                      totalTokens: usage.total_tokens || usage.prompt_tokens,
                    });
                  }
                  
                  return thread;
                } catch (toolError) {
                  this.error(`Error executing tool ${toolName}`, { toolError, toolArgs });
                  throw toolError;
                }
              }
            }
          } catch (parsingError) {
            this.error("Error parsing failed_generation", { 
              parsingError, 
              failed_generation: error.error.failed_generation 
            });
            // Re-throw the original error if we can't parse the failed_generation
            throw error;
          }
        }
        // Try to extract failed_generation from error message if not in error.error structure
        else if (error?.message && error.message.includes('failed_generation') && 
                 tools && tools.length > 0) {
          this.debug("Attempting to extract failed_generation from error message", { 
            message: error.message 
          });
          
          try {
            // Try to find a JSON object in the error message
            const jsonMatch = error.message.match(/failed_generation['":\s]+({[\s\S]*?})(?:\s|$)/i);
            if (jsonMatch && jsonMatch[1]) {
              const failedGeneration = JSON.parse(jsonMatch[1]);
              
              // Extract tool name
              const toolName = failedGeneration.name || 
                              (failedGeneration.function && failedGeneration.function.name);
              
              if (toolName) {
                // Extract arguments
                let toolArgs: any = {};
                
                if (failedGeneration.arguments) {
                  toolArgs = typeof failedGeneration.arguments === 'string' 
                    ? JSON.parse(failedGeneration.arguments) 
                    : failedGeneration.arguments;
                } else if (failedGeneration.parameters) {
                  toolArgs = typeof failedGeneration.parameters === 'string' 
                    ? JSON.parse(failedGeneration.parameters) 
                    : failedGeneration.parameters;
                } else if (failedGeneration.function) {
                  if (failedGeneration.function.arguments) {
                    toolArgs = typeof failedGeneration.function.arguments === 'string' 
                      ? JSON.parse(failedGeneration.function.arguments) 
                      : failedGeneration.function.arguments;
                  } else if (failedGeneration.function.parameters) {
                    toolArgs = typeof failedGeneration.function.parameters === 'string' 
                      ? JSON.parse(failedGeneration.function.parameters) 
                      : failedGeneration.function.parameters;
                  }
                }
                
                // Find the matching tool and attempt to execute it
                const matchingTool = tools.find(t => t.getName() === toolName);
                if (matchingTool) {
                  this.debug(`Found tool ${toolName} from error message, attempting to execute`, toolArgs);
                  
                  // Generate a unique tool use ID
                  const toolUseId = nanoid();
                  
                  // Create a tool call object
                  const toolCall = {
                    toolUseId,
                    name: toolName,
                    arguments: toolArgs
                  };
                  
                  // Execute the tool
                  const toolResult = await matchingTool.executeTool(toolArgs, toolCall);
                  
                  // Create messages and add to thread as in the other error handler
                  // Extract the result
                  const result = toolResult.success ? toolResult.result : null;
                  
                  // Create content for the final message
                  let content = `I'll help with that using the ${toolName} tool.\n\n`;
                  
                  if (result !== null) {
                    if (typeof result === 'object') {
                      content += `Here's the result: ${JSON.stringify(result, null, 2)}`;
                    } else {
                      content += `The result is: ${result}`;
                    }
                  } else if (toolResult.error) {
                    content += `There was an error executing the tool: ${toolResult.error}`;
                  }
                  
                  // Create and add messages to the thread
                  const assistantMessage = new Message({
                    role: "assistant",
                    content: `I'll help with that using the ${toolName} tool.`,
                    toolCalls: [toolCall],
                    cache: this.cache,
                  });
                  
                  const toolResultMessage = new Message({
                    role: "user",
                    content: "",
                    toolResults: [{
                      toolUseId,
                      name: toolName,
                      result: result
                    }],
                    cache: this.cache,
                  });
                  
                  const finalMessage = new Message({
                    role: "assistant",
                    content,
                    cache: this.cache,
                  });
                  
                  thread.push(assistantMessage);
                  thread.push(toolResultMessage);
                  thread.push(finalMessage);
                  
                  this.debug("Successfully executed tool from error message");
                  return thread;
                }
              }
            } else {
              // Try to extract tool name and args from error message with regex
              const nameMatch = error.message.match(/name['"]?\s*:\s*['"]([^'"]+)['"]/i);
              const argumentsMatch = error.message.match(/arguments['"]?\s*:\s*['"]?({[^}]+})['"]?/i);
              
              if (nameMatch && nameMatch[1]) {
                const toolName = nameMatch[1];
                let toolArgs = {};
                
                if (argumentsMatch && argumentsMatch[1]) {
                  try {
                    toolArgs = JSON.parse(argumentsMatch[1]);
                  } catch (e) {
                    this.error("Failed to parse arguments from error message", { 
                      args: argumentsMatch[1], error: e 
                    });
                  }
                }
                
                const matchingTool = tools.find(t => t.getName() === toolName);
                if (matchingTool) {
                  // Execute the tool as in the previous case
                  // ... similar implementation as above
                  this.debug(`Found tool ${toolName} from error message regex, attempting to execute`, toolArgs);
                  
                  // Generate a unique tool use ID
                  const toolUseId = nanoid();
                  
                  // Create the tool call and execute it (simplified to not duplicate code)
                  const toolCall = { toolUseId, name: toolName, arguments: toolArgs };
                  const toolResult = await matchingTool.executeTool(toolArgs, toolCall);
                  const result = toolResult.success ? toolResult.result : null;
                  
                  // Add the appropriate messages to the thread
                  thread.push(new Message({
                    role: "assistant",
                    content: `I'll help with that using the ${toolName} tool.`,
                    toolCalls: [toolCall],
                    cache: this.cache,
                  }));
                  
                  thread.push(new Message({
                    role: "user",
                    content: "",
                    toolResults: [{
                      toolUseId,
                      name: toolName,
                      result: result
                    }],
                    cache: this.cache,
                  }));
                  
                  let content = `I'll help with that using the ${toolName} tool.\n\n`;
                  if (result !== null) {
                    content += typeof result === 'object' 
                      ? `Here's the result: ${JSON.stringify(result, null, 2)}` 
                      : `The result is: ${result}`;
                  } else if (toolResult.error) {
                    content += `There was an error executing the tool: ${toolResult.error}`;
                  }
                  
                  thread.push(new Message({
                    role: "assistant",
                    content,
                    cache: this.cache,
                  }));
                  
                  this.debug("Successfully executed tool from error message regex match");
                  return thread;
                }
              }
            }
          } catch (parsingError) {
            this.error("Error extracting failed_generation from error message", { 
              parsingError, 
              message: error.message 
            });
          }
        }
        
        // If we couldn't handle the error, re-throw it
        throw error;
      }

      // Log usage metrics if available
      if (response && response.usage) {
        const usage = response.usage as {
          prompt_tokens: number;
          completion_tokens: number;
          total_tokens: number;
        };

        this.debug("Cerebras API usage metrics", {
          inputTokens: usage.prompt_tokens,
          outputTokens: usage.completion_tokens,
          totalTokens: usage.total_tokens,
        });
      }

      return thread;
    } catch (error) {
      this.error("Error sending message to Cerebras API", { error });
      throw error;
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    this.debug("Streaming message to Cerebras API", { model: this.model });
    
    // Check if we have a completely new conversation
    const isNewConversation = thread.all().length <= 1;
    
    if (isNewConversation) {
      // Reset all tracking for new streamed conversations
      this.debug("Starting new streamed conversation, resetting all tracking");
      this.recentToolCalls = [];
      this.totalToolCallsCounter = 0;
      this.toolCallTracker = {};
    }
    
    // For safety, examine the existing thread for already executed tool calls
    if (!isNewConversation) {
      // Track all existing tool calls in the thread for streaming
      let existingToolCalls = 0;
      
      for (const msg of thread.all()) {
        if (msg.toolCalls && msg.toolCalls.length > 0) {
          existingToolCalls += msg.toolCalls.length;
          
          // Also track each tool call's signature
          for (const call of msg.toolCalls) {
            const argsHash = this.hashToolCallArgs(call.arguments);
            const callKey = `${call.name}:${argsHash}`;
            this.toolCallTracker[callKey] = (this.toolCallTracker[callKey] || 0) + 1;
          }
        }
      }
      
      // Use this to initialize our counter if it's not already set
      if (this.totalToolCallsCounter < existingToolCalls) {
        this.totalToolCallsCounter = existingToolCalls;
        this.debug("Updated tool call counter for streaming from thread history", { count: existingToolCalls });
      }
      
      // Log the current state of tool counts
      this.debug("Current tool call counts", {
        totalToolCallsCounter: this.totalToolCallsCounter,
        maxLimit: this.maxTotalToolCalls
      });
    }

    // Format messages for Cerebras API
    let messages = this.formatMessagesForAPI(thread);
    
    // Check if the last message is a tool result, indicating we should simplify history
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'tool') {
      this.debug("Simplifying message history after tool result");
      messages = this.simplifyMessagesAfterToolCall(messages);
    }

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (typeof msg.content === "string" ? msg.content.length : 0),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
        stream: true,
      };

      // Add max tokens if specified
      if (this.maxTokens) {
        params.max_tokens = this.maxTokens;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools?.() || [];
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        
        // Handle tool choice based on parallel tool call setting
        if (this.disableParallelToolCalls) {
          params.parallel_tool_calls = false; // Forces model to use exactly one tool
          params.tool_choice = "auto"; 
        } else {
          params.tool_choice = "auto"; // Default behavior
        }
      } else if (this.responseFormatSchema) {
        // Only apply response_format if NO tools are being used
        params.response_format = {
          type: "json_schema",
          json_schema: {
            name: this.responseFormatName,
            strict: this.responseFormatStrict,
            schema: this.responseFormatSchema
          }
        };
        this.debug("Applying structured response_format for streaming", { name: this.responseFormatName, strict: this.responseFormatStrict });
      }

      // Create a new message to add to the thread
      const message = new Message({
        role: "assistant",
        content: "",
        cache: this.cache,
      });

      // Make the streaming API call
      const streamResponse = await this.client.chat.completions.create({
        ...params,
        stream: true,
      });

      // Convert the response to a proper AsyncIterable
      const stream = this.createAsyncIterableFromResponse(streamResponse);

      // Create a streaming content generator
      const streamContent = this.createStreamGenerator(stream, message);

      // Yield the stream
      yield { stream: streamContent, message };

      // Add the message to the thread
      thread.push(message);

      // Return the updated thread
      return thread;
    } catch (error) {
      this.error("Error streaming message to Cerebras API", { error });
      throw error;
    }
  }

  /**
   * Create a stream generator that handles updates to the message
   */
  private async *createStreamGenerator(
    stream: AsyncIterable<CerebrasStreamingResponse>,
    message: Message
  ): AsyncGenerator<string, void> {
    let toolCallsStarted = false;
    let currentToolCalls: any[] = [];

    try {
      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta;

        if (!delta) continue;

        // Handle content updates
        if (delta.content) {
          // Create a new message with updated content
          const updatedMessage = new Message({
            role: "assistant",
            content: (message.content || "") + delta.content,
            toolCalls: message.toolCalls,
            cache: this.cache,
          });
          
          // Copy the updated message properties
          Object.assign(message, updatedMessage);
          
          yield delta.content;
        }

        // Handle tool call updates
        if (delta.tool_calls) {
          if (!toolCallsStarted) {
            // First tool call chunk
            toolCallsStarted = true;
            currentToolCalls = delta.tool_calls.map((call: any) => ({ ...call }));
          } else {
            // Subsequent tool call chunks - need to merge them
            for (const toolCall of delta.tool_calls) {
              const existingCall = currentToolCalls.find((c: any) => c.index === toolCall.index);
              
              if (existingCall) {
                // Update function content if provided
                if (toolCall.function) {
                  if (!existingCall.function) {
                    existingCall.function = {};
                  }
                  
                  if (toolCall.function.name && !existingCall.function.name) {
                    existingCall.function.name = toolCall.function.name;
                  }
                  
                  // Handle arguments field
                  if (toolCall.function.arguments) {
                    existingCall.function.arguments = (existingCall.function.arguments || "") + 
                      toolCall.function.arguments;
                  }
                  // Handle parameters field (if API is using that instead)
                  else {
                    // Use type assertion to safely access any potential parameters field
                    const func = toolCall.function as any;
                    if (func.parameters) {
                      // Convert parameters to arguments field format
                      const parameters = typeof func.parameters === 'string' 
                        ? func.parameters 
                        : JSON.stringify(func.parameters);
                      
                      existingCall.function.arguments = (existingCall.function.arguments || "") + parameters;
                    }
                  }
                }
              } else {
                // New tool call
                currentToolCalls.push({ ...toolCall });
              }
            }
          }
        }
      }

      // Process the tool calls after the stream completes
      if (currentToolCalls.length > 0) {
        const parsedToolCalls = this.parseToolCalls(currentToolCalls);
        
        // Update total tool call counter for stream
        if (parsedToolCalls) {
          this.updateToolCallCounter(parsedToolCalls);
        }
        
        // Check if we've exceeded the total tool call limit
        if (this.totalToolCallsCounter > this.maxTotalToolCalls) {
          this.debug("Exceeded maximum total tool calls limit in stream", {
            count: this.totalToolCallsCounter,
            limit: this.maxTotalToolCalls,
            model: this.model
          });
          
          yield "\n\nI've reached the maximum number of tool calls allowed for this conversation. Let me summarize without using more tools.";
          
          // Create a message with no tool calls
          const updatedMessage = new Message({
            role: "assistant",
            content: message.content || "I've reached the maximum number of tool calls allowed. Let me help you directly.",
            cache: this.cache,
          });
          
          // Copy the updated message properties
          Object.assign(message, updatedMessage);
          return;
        }
        
        // Check for any repeated tool calls - with our updated policy,
        // we allow the first occurrence but filter out repeated calls
        let finalToolCalls: ToolCall[] = [];
        if (parsedToolCalls) {
          // Check each tool call to see if it's already been used
          finalToolCalls = parsedToolCalls.filter(toolCall => {
            const isRepeated = this.isRepeatedToolCall(toolCall.name, toolCall.arguments);
            if (isRepeated) {
              this.debug(`Filtering out repeated tool call in stream: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: false
              });
            } else {
              this.debug(`Allowing tool call in stream: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: true
              });
            }
            return !isRepeated;
          });
          
          // Log if we found any repeated calls
          if (finalToolCalls.length < parsedToolCalls.length) {
            this.debug("Filtered out repeated tool calls in stream", {
              modelName: this.model,
              originalCount: parsedToolCalls.length,
              filteredCount: finalToolCalls.length,
              toolNames: parsedToolCalls.map(tc => tc.name),
              allowedTools: finalToolCalls.map(tc => tc.name)
            });
            
            // If all tool calls were filtered out, yield a message explaining why
            if (finalToolCalls.length === 0) {
              yield "\n\nI noticed I would be repeating tool calls that were already used. Let me approach this differently without using tools.";
              
              // Just update content, no tool calls
              const updatedMessage = new Message({
                role: "assistant",
                content: (message.content || "") + "\n\nI noticed I would be repeating tool calls. Let me approach this differently without using tools.",
                cache: this.cache,
              });
              
              // Copy the updated message properties
              Object.assign(message, updatedMessage);
              return;
            }
          }
        }
        
        // Create a new message with the filtered tool calls
        const updatedMessage = new Message({
          role: "assistant",
          content: message.content || "",
          toolCalls: finalToolCalls,
          cache: this.cache,
        });
        
        // Copy the updated message properties
        Object.assign(message, updatedMessage);
      } else {
        // If no tool calls were found directly, check for embedded ones in content
        const embeddedToolCalls = this.parseEmbeddedToolCalls(message.content || "");
        if (embeddedToolCalls) {
          // Update tool call counter
          this.updateToolCallCounter(embeddedToolCalls);
          
          // Check for loop or limit exceeded
          if (this.totalToolCallsCounter > this.maxTotalToolCalls) {
            // Just return a text message, no tool calls
            const limitMessage = new Message({
              role: "assistant",
              content: message.content || "I've reached the maximum number of tool calls allowed. Let me help you directly.",
              cache: this.cache,
            });
            
            Object.assign(message, limitMessage);
            return;
          }
          
          // Filter out any repeated tool calls from the extracted ones
          const safeToolCalls = embeddedToolCalls.filter(toolCall => {
            const isRepeated = this.isRepeatedToolCall(toolCall.name, toolCall.arguments);
            if (isRepeated) {
              this.debug(`Filtering out repeated embedded tool call: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: false
              });
            } else {
              this.debug(`Allowing embedded tool call: ${toolCall.name}`, {
                toolUseId: toolCall.toolUseId,
                isFirstUse: true
              });
            }
            return !isRepeated;
          });
          
          // Update message based on filtering results
          if (safeToolCalls.length > 0) {
            // Some tool calls are safe to use
            const updatedMessage = new Message({
              role: "assistant",
              content: "", // Empty content since these were embedded
              toolCalls: safeToolCalls,
              cache: this.cache,
            });
            
            Object.assign(message, updatedMessage);
          } else if (embeddedToolCalls.length > 0) {
            // All embedded tool calls were repeats
            const alternativeMessage = new Message({
              role: "assistant",
              content: "I noticed I would be using repeated tool calls. Let me help you directly instead.",
              cache: this.cache,
            });
            
            Object.assign(message, alternativeMessage);
          }
        }
      }
    } catch (error) {
      this.error("Error processing Cerebras stream", { error });
      throw error;
    }
  }

  /**
   * Format messages from the Thread object to the Cerebras API format
   */
  private formatMessagesForAPI(thread: Thread): any[] {
    const messages: any[] = [];

    for (const message of thread.all()) {
      // First preprocess any content to handle special field references
      const processedContent = message.content 
        ? this.preprocessFieldReferences(message.content) 
        : message.content;
      
      // Process attachments if they exist
      if (message.attachments && message.attachments.length > 0) {
        const contentParts: any[] = [];
        
        // Add text content if it exists
        if (processedContent) {
          contentParts.push({
            type: "text",
            text: processedContent,
          });
        }
        
        // Add each attachment
        for (const attachment of message.attachments) {
          if (attachment.type === "image" && attachment.source.uri) {
            contentParts.push({
              type: "image_url",
              image_url: {
                url: attachment.source.uri,
                detail: "auto",
              },
            });
          }
        }
        
        // Cerebras has similar typing requirements as OpenAI for different roles
        if (message.role === "user") {
          messages.push({
            role: "user",
            content: contentParts,
          });
        } else if (message.role === "assistant") {
          messages.push({
            role: "assistant",
            content: contentParts,
          });
        } else if (message.role === "system") {
          messages.push({
            role: "system",
            content: contentParts,
          });
        }
        continue;
      }

      // Handle tool results (these are created from user messages with toolResults)
      if (message.toolResults && message.toolResults.length > 0) {
        // First add the user message if it has content
        if (processedContent) {
          messages.push({
            role: "user",
            content: processedContent,
          });
        }

        // Then add each tool result as a separate message
        for (const toolResult of message.toolResults) {
          let toolResultContent: string;
          
          // Ensure proper stringification of tool results
          if (typeof toolResult.result === 'string') {
            toolResultContent = toolResult.result;
          } else {
            try {
              toolResultContent = JSON.stringify(toolResult.result);
            } catch (e) {
              this.error("Error stringifying tool result", { error: e, result: toolResult.result });
              toolResultContent = String(toolResult.result);
            }
          }
          
          messages.push({
            role: "tool",
            tool_call_id: toolResult.toolUseId,
            content: toolResultContent,
          });
        }
        continue;
      }

      // Handle tool calls in assistant messages
      if (message.role === "assistant" && message.toolCalls && message.toolCalls.length > 0) {
        const toolCalls = message.toolCalls.map(call => {
          // Ensure arguments is properly stringified
          let argumentsStr = "{}"; // Default to empty object
          
          // Workaround for 'arguments' error - always use a valid JSON object
          try {
            // Handle different argument types
            if (typeof call.arguments === 'string') {
              // Check if it's already valid JSON
              try {
                JSON.parse(call.arguments);
                argumentsStr = call.arguments;
              } catch (e) {
                // Not valid JSON, wrap it
                argumentsStr = JSON.stringify({ value: call.arguments });
              }
            } else if (call.arguments && typeof call.arguments === 'object') {
              // For objects, stringify them safely
              argumentsStr = JSON.stringify(call.arguments);
            }
          } catch (e) {
            this.error("Error processing tool call arguments", { 
              error: e, 
              name: call.name,
              args: typeof call.arguments === 'object' ? 'object' : call.arguments
            });
          }
          
          // Log the tool call for debugging
          this.debug(`Tool call for ${call.name}`, {
            name: call.name,
            argumentsStr,
          });
          
          return {
            id: call.toolUseId,
            type: "function" as const,
            function: {
              name: call.name,
              arguments: argumentsStr,
            },
          };
        });

        messages.push({
          role: "assistant",
          content: processedContent || null,
          tool_calls: toolCalls,
        });
        continue;
      }

      // Default case for simple messages
      if (message.role === "user") {
        messages.push({
          role: "user",
          content: processedContent || "",
        });
      } else if (message.role === "assistant") {
        messages.push({
          role: "assistant",
          content: processedContent || "",
        });
      } else if (message.role === "system") {
        messages.push({
          role: "system",
          content: processedContent || "",
        });
      }
    }

    return messages;
  }

  /**
   * Create tool definitions for the Cerebras API
   */
  private createToolDefinitions(tools: Tool[]): any[] {
    return tools.map(tool => {
      const zodParams = tool.getParameters();
      // Convert Zod schema to JSON schema
      const jsonSchemaFromZod = zodToJsonSchema(zodParams) as any;

      // Use properties directly from the converted schema.
      // zodToJsonSchema should handle correct type definitions, including items for arrays.
      const properties = jsonSchemaFromZod.properties || {};
      let required = jsonSchemaFromZod.required || [];

      // Filter 'required' to ensure all fields listed actually exist in 'properties'.
      // This is a good sanity check and ensures robustness.
      if (Array.isArray(required)) {
        required = required.filter(
          (reqField: string) => typeof reqField === 'string' && properties.hasOwnProperty(reqField)
        );
      } else {
        // Ensure 'required' is always an array, even if originally undefined or malformed.
        required = [];
      }

      const apiParameters = {
        type: jsonSchemaFromZod.type || "object", // zod-to-json-schema usually sets type: 'object' for z.object()
        properties: properties, // Use properties directly from zodToJsonSchema output
        required: required
        // Other JSON Schema fields like 'description', 'definitions' could be included
        // if relevant and output by zodToJsonSchema and needed by the API.
        // For now, focusing on the core: type, properties, required.
      };

      // Log the final tool parameters being prepared for the API call
      this.debug(`Tool definition parameters for ${tool.getName()}`, apiParameters);

      return {
        type: "function",
        function: {
          name: tool.getName(),
          strict: true,
          description: tool.getDescription(), // This is the description of the tool/function itself
          parameters: apiParameters // This is the JSON schema for the arguments the function expects
        },
      };
    });
  }

  /**
   * Parse tool calls from the Cerebras API response
   */
  private parseToolCalls(toolCalls?: any[]): ToolCall[] | undefined {
    if (!toolCalls || toolCalls.length === 0) {
      return undefined;
    }

    return toolCalls.map(toolCall => {
      if (toolCall.type === 'function' && toolCall.function?.name) {
        try {
          let args = {};
          
          // Handle various argument formats that Cerebras might return
          if (toolCall.function.arguments) {
            if (typeof toolCall.function.arguments === 'string') {
              try {
                // Try to parse JSON string
                args = JSON.parse(toolCall.function.arguments);
              } catch (e) {
                // If parsing fails, use the string directly
                this.debug("Failed to parse tool call arguments as JSON", {
                  arguments: toolCall.function.arguments,
                  error: e
                });
                args = { value: toolCall.function.arguments };
              }
            } else if (typeof toolCall.function.arguments === 'object') {
              // Already an object
              args = toolCall.function.arguments;
            }
          }
          
          // Handle if the model might be returning in 'parameters' format
          // instead of 'arguments' (based on the error we're seeing)
          else if (toolCall.function.parameters) {
            if (typeof toolCall.function.parameters === 'string') {
              try {
                args = JSON.parse(toolCall.function.parameters);
              } catch (e) {
                // If parsing fails, use the string directly
                this.debug("Failed to parse tool call parameters as JSON", {
                  parameters: toolCall.function.parameters,
                  error: e
                });
                args = { value: toolCall.function.parameters };
              }
            } else if (typeof toolCall.function.parameters === 'object') {
              args = toolCall.function.parameters;
            }
          }
          
          return {
            toolUseId: toolCall.id || nanoid(),
            name: toolCall.function.name,
            arguments: args,
          };
        } catch (error) {
          this.error("Error parsing tool call arguments", {
            error,
            toolCall,
          });
          return {
            toolUseId: toolCall.id || nanoid(),
            name: toolCall.function.name,
            arguments: {},
          };
        }
      }
      
      this.error("Unknown tool call type", { toolCall });
      return {
        toolUseId: toolCall.id || nanoid(),
        name: toolCall.function?.name || "unknown",
        arguments: {},
      };
    });
  }

  /**
   * Check if a tool should be excluded from the tool call limit count
   * Some utility tools like SuggestNextStepsTool are meant to be called multiple times
   * and shouldn't count toward the limit
   */
  private isUtilityTool(toolName: string): boolean {
    // List of utility tools that shouldn't count toward the limit
    const utilityTools = ['SuggestNextStepsTool'];
    return utilityTools.includes(toolName);
  }

  /**
   * Update the tool call counter appropriately based on the tools being called
   * Utility tools don't count toward the limit
   */
  private updateToolCallCounter(toolCalls: ToolCall[]): number {
    // Filter out utility tools that shouldn't count toward the limit
    const countableToolCalls = toolCalls.filter(call => !this.isUtilityTool(call.name));
    
    // Update the counter with only the countable tools
    this.totalToolCallsCounter += countableToolCalls.length;
    
    // Log what's being counted and what's not
    if (countableToolCalls.length < toolCalls.length) {
      this.debug("Some tools excluded from call limit", {
        totalCalls: toolCalls.length,
        countableCalls: countableToolCalls.length,
        excludedTools: toolCalls
          .filter(call => this.isUtilityTool(call.name))
          .map(call => call.name)
      });
    }
    
    return this.totalToolCallsCounter;
  }

  /**
   * Convert a Cerebras streaming response to an AsyncIterable
   */
  private createAsyncIterableFromResponse(response: any): AsyncIterable<CerebrasStreamingResponse> {
    // If response is already an AsyncIterable, return it
    if (response[Symbol.asyncIterator]) {
      return response as AsyncIterable<CerebrasStreamingResponse>;
    }
    
    // Otherwise, create an AsyncIterable that yields the response
    return {
      [Symbol.asyncIterator]: async function* () {
        // If streaming is not properly supported, yield a synthetic response
        yield {
          id: typeof response.id === 'string' ? response.id : 'stream-response',
          object: 'chat.completion.chunk',
          created: Date.now(),
          model: typeof response.model === 'string' ? response.model : 'unknown',
          choices: [{
            delta: {
              content: typeof response.choices?.[0]?.message?.content === 'string' 
                ? response.choices[0].message.content 
                : ''
            },
            index: 0,
            finish_reason: 'stop'
          }]
        } as CerebrasStreamingResponse;
      }
    };
  }

  /**
   * Parse message content for embedded JSON tool calls
   * This handles cases where the model returns tool calls as JSON directly in the message content
   * 
   * @param content The message content to parse
   * @returns ToolCall[] array if tool calls were found, undefined otherwise
   */
  private parseEmbeddedToolCalls(content: string): ToolCall[] | undefined {
    if (!content) return undefined;
    
    // Try to extract JSON patterns from the content
    const toolCalls: ToolCall[] = [];
    
    try {
      // Handle escaped JSON that might be present in the content
      let processedContent = content;
      
      // Replace escaped quotes and backslashes to make parsing easier
      processedContent = processedContent
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
      
      // First check for direct function call format like "type": "function", "name": "..."
      if (processedContent.includes('"type"') && processedContent.includes('"name"')) {
        try {
          const parsed = JSON.parse(processedContent);
          
          // Handle format: {"type": "function", "name": "ToolName", "parameters": {...}}
          if (parsed && 
              typeof parsed === 'object' && 
              parsed.type === 'function' && 
              parsed.name) {
            
            this.debug("Found function-style tool call in content", parsed);
            
            const toolCall: ToolCall = {
              toolUseId: nanoid(),
              name: parsed.name,
              arguments: parsed.parameters || {}
            };
            
            toolCalls.push(toolCall);
            return toolCalls;
          }
        } catch (e) {
          // Continue to other formats if this fails
          this.debug("Failed to parse function-style tool call", { 
            error: e, 
            content: processedContent.substring(0, 100) 
          });
        }
      }
      
      // Next, check if the entire content is valid JSON with name and parameters fields
      try {
        const parsed = JSON.parse(processedContent);
        
        // If the parsed content is an object with name and parameters fields,
        // it's likely a tool call embedded directly in the content
        if (parsed && 
            typeof parsed === 'object' && 
            parsed.name && 
            (parsed.parameters || parsed.arguments)) {
          
          this.debug("Found direct tool call JSON in message content", parsed);
          
          // Handle parameters that might include escaped JSON
          let args = parsed.parameters || parsed.arguments || {};
          
          // If args is a string that contains JSON, try to parse it
          if (typeof args === 'string') {
            try {
              // Handle special case where parameters might be JSON with escaped sequences
              // For example: @{{{\"type\":\"field\",\"name\":\"Email\"...}}}
              const jsonMatch = args.match(/@\{\{\{(.*?)\}\}\}/);
              if (jsonMatch && jsonMatch[1]) {
                // Replace escaped quotes and try to parse
                const cleanJson = jsonMatch[1].replace(/\\"/g, '"');
                try {
                  args = JSON.parse(cleanJson);
                } catch (e) {
                  // Keep as is if we can't parse
                }
              } else {
                // Try to parse the entire string as JSON
                args = JSON.parse(args);
              }
            } catch (e) {
              // Keep as is if we can't parse
            }
          }
          
          // Create a tool call from this format
          const toolCall: ToolCall = {
            toolUseId: nanoid(),
            name: parsed.name,
            arguments: args
          };
          
          toolCalls.push(toolCall);
          return toolCalls;
        }
      } catch (e) {
        // Not valid JSON, continue with regex-based extraction
      }
      
      // Special case for SuggestNextStepsTool which has a distinct format
      const suggestToolRegex = /"name":\s*"SuggestNextStepsTool"/i;
      if (suggestToolRegex.test(processedContent)) {
        try {
          // Extract parameters if present
          const paramsMatch = processedContent.match(/"parameters":\s*(\{[^}]*\})/);
          const params = paramsMatch && paramsMatch[1] ? JSON.parse(paramsMatch[1]) : {};
          
          const toolCall: ToolCall = {
            toolUseId: nanoid(),
            name: "SuggestNextStepsTool",
            arguments: params
          };
          
          this.debug("Found SuggestNextStepsTool in content", toolCall);
          toolCalls.push(toolCall);
          return toolCalls;
        } catch (e) {
          // If extraction fails, create a minimal tool call
          const toolCall: ToolCall = {
            toolUseId: nanoid(),
            name: "SuggestNextStepsTool",
            arguments: { extra: {} }
          };
          
          this.debug("Created minimal SuggestNextStepsTool call", { error: e });
          toolCalls.push(toolCall);
          return toolCalls;
        }
      }
      
      // Special case for NormalizeTool which is frequently used with field references
      const normalizeToolRegex = /"name":\s*"NormalizeTool"/i;
      if (normalizeToolRegex.test(processedContent)) {
        try {
          // Extract parameters if present
          const paramsMatch = processedContent.match(/"parameters":\s*(\{[^}]*\})/);
          const rawParams = paramsMatch && paramsMatch[1] ? paramsMatch[1] : "{}";
          
          // Handle special characters and escape sequences in the raw JSON
          const cleanParams = rawParams
            .replace(/\\"/g, '"') 
            .replace(/\\\\/g, '\\');
          
          let params: Record<string, any> = {};
          try {
            params = JSON.parse(cleanParams);
          } catch (e) {
            // If parsing fails, try to extract individual fields
            const sourceFieldMatch = rawParams.match(/"sourceField"\s*:\s*"([^"]+)"/);
            const formatMatch = rawParams.match(/"format"\s*:\s*"([^"]+)"/);
            const summaryMatch = rawParams.match(/"summary"\s*:\s*"([^"]+)"/);
            
            params = {
              sourceField: sourceFieldMatch && sourceFieldMatch[1] ? sourceFieldMatch[1] : "",
              format: formatMatch && formatMatch[1] ? formatMatch[1] : "",
              summary: summaryMatch && summaryMatch[1] ? summaryMatch[1] : "Normalize field"
            };
          }
          
          // Process any @{{{...}}} field references in parameters
          for (const key in params) {
            if (typeof params[key] === 'string' && params[key].includes('@{{{')) {
              params[key] = this.preprocessFieldReferences(params[key]);
            }
          }
          
          const toolCall: ToolCall = {
            toolUseId: nanoid(),
            name: "NormalizeTool",
            arguments: params
          };
          
          this.debug("Found NormalizeTool in content", toolCall);
          toolCalls.push(toolCall);
          return toolCalls;
        } catch (e) {
          // If extraction fails, log the error but don't create a minimal call
          // since NormalizeTool requires specific parameters
          this.error("Failed to parse NormalizeTool call", { error: e, content: processedContent });
        }
      }
      
      // If we couldn't parse the entire content as JSON, look for valid JSON objects within it
      const jsonPattern = /{(?:[^{}]|(?:{[^{}]*}))*}/g;
      let match;
      
      while ((match = jsonPattern.exec(processedContent)) !== null) {
        try {
          const jsonStr = match[0];
          const parsed = JSON.parse(jsonStr);
          
          if (parsed && 
              typeof parsed === 'object' && 
              (parsed.name || (parsed.function && parsed.function.name)) && 
              (parsed.parameters || parsed.arguments || 
               (parsed.function && (parsed.function.parameters || parsed.function.arguments)))) {
            
            // Determine the tool name
            const toolName = parsed.name || (parsed.function && parsed.function.name);
            
            // Determine the arguments
            let args = {};
            if (parsed.parameters) {
              args = parsed.parameters;
            } else if (parsed.arguments) {
              args = parsed.arguments;
            } else if (parsed.function) {
              args = parsed.function.parameters || parsed.function.arguments || {};
            }
            
            // If args is a string, try to parse it as JSON
            if (typeof args === 'string') {
              try {
                // Handle special case where parameters might be JSON with escaped sequences
                const jsonMatch = args.match(/@\{\{\{(.*?)\}\}\}/);
                if (jsonMatch && jsonMatch[1]) {
                  // Replace escaped quotes and try to parse
                  const cleanJson = jsonMatch[1].replace(/\\"/g, '"');
                  try {
                    args = JSON.parse(cleanJson);
                  } catch (e) {
                    // Keep as is if we can't parse
                  }
                } else {
                  // Try to parse the entire string as JSON
                  args = JSON.parse(args);
                }
              } catch (e) {
                // Keep as is if we can't parse
              }
            }
            
            // Format as internal ToolCall
            const toolCall: ToolCall = {
              toolUseId: nanoid(),
              name: toolName,
              arguments: args
            };
            
            toolCalls.push(toolCall);
            this.debug(`Found embedded tool call for ${toolCall.name}`, toolCall);
          }
        } catch (parseError) {
          // Skip invalid JSON
          continue;
        }
      }
      
      return toolCalls.length > 0 ? toolCalls : undefined;
    } catch (error) {
      this.error("Error parsing embedded tool calls", { error, content });
      return undefined;
    }
  }

  /**
   * Preprocess tool definitions to handle special field references like @{{{...}}}
   */
  private preprocessToolDefinitions(tools: Tool[]): Tool[] {
    // Create a deep copy of the tools to avoid modifying the originals
    return tools.map(tool => {
      // For each tool, we want to analyze its parameters and preprocess any special patterns
      // This is a workaround for the specific format issue with @{{{...}}} in the example
      
      // Log the tool for debugging
      this.debug(`Preprocessing tool ${tool.getName()}`, {
        name: tool.getName(),
        description: tool.getDescription(),
        parameters: tool.getParameters()
      });
      
      // Return the tool as-is for now
      // We'll handle special patterns in the createToolDefinitions and formatMessagesForAPI methods
      return tool;
    });
  }

  /**
   * Handle special field references in content
   */
  private preprocessFieldReferences(content: string): string {
    if (!content) return content;
    
    // Handle special @{{{...}}} field references
    // This is a workaround for the specific format in the example
    try {
      // Replace @{{{...}}} with a simpler format that's less likely to cause issues
      return content.replace(/@\{\{\{(.*?)\}\}\}/g, (match, p1) => {
        try {
          // Try to parse the JSON inside and simplify it
          const parsed = JSON.parse(p1.replace(/\\"/g, '"'));
          if (parsed && parsed.type === 'field' && parsed.name) {
            // Replace with a simpler format that references the field name
            return `[Field:${parsed.name}]`;
          }
        } catch (e) {
          // If parsing fails, just return the field name part if we can extract it
          const nameMatch = p1.match(/name\\?"?\s*:\s*\\?"?([^\\",}]+)/);
          if (nameMatch && nameMatch[1]) {
            return `[Field:${nameMatch[1]}]`;
          }
        }
        // If all else fails, return the original match
        return match;
      });
    } catch (e) {
      this.error("Error preprocessing field references", { error: e, content });
      return content;
    }
  }
} 